/**
 * @description Hook utilizado para manipular as solicitações de exame (entidade que envolve os exames solicitados)
 * @author Diego Sabino Amorim de Araújo
 * @file useSolicitacaoExameService.js
 * @license Copyright (c) 2022 SIGEP DIGETI ITEP/RN
 */

import actionTypes from 'utils/actionTypes'
import { useDispatch } from 'react-redux'
import { useFetch } from './useFetch'

export const useSolicitacaoExameService = () => {
  const fetch = useFetch()
  const dispatch = useDispatch()

  const listarSolicitacoesDeCasoGlobal = async (idCaso, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`solicitacoesexame/deCaso/${idCaso}`)
      dispatch({
        type: actionTypes.SET_SOLICITACOES_CASO,
        solicitacoes: response.data.solicitacoesExame,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarSolicitacoesDeCasoAsync = async (idCaso, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`solicitacoesexame/deCaso/${idCaso}`)
      return response.data.solicitacoesExame
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarSolicitacaoExameGlobal = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`solicitacoesexame/detalharSolicitacaoExame/${id}`)
      dispatch({
        type: actionTypes.FETCH_SOLICITACAO_EXAME,
        solicitacaoExame: response.data.solicitacaoExame,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarSolicitacaoExame = async (id, setSolicitacaoExame, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`solicitacoesexame/detalharSolicitacaoExame/${id}`)
      setSolicitacaoExame(response.data.solicitacaoExame)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarSolicitacaoExameAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`solicitacoesexame/detalharSolicitacaoExame/${id}`)
      return response.data.solicitacaoExame
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarTiposSolicitacoesDeExameGlobal = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`solicitacoesexame/tipossolicitacao/`)
      dispatch({
        type: actionTypes.TIPOS_SOLICITACOES,
        tiposSolicitacoes: response.data.tiposSolicitacao,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const deletarSolicitacaoExame = async (id, setProcessando) => {
    if (setProcessando) setProcessando(true)
    await fetch.del(`/solicitacoesexame/${id}/`)
    if (setProcessando) setProcessando(false)
  }

  const cadastrarSolicitacaoExame = async (values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.postOpen('/solicitacoesexame/', values)
      return response.data.solicitacaoExame
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const cadastrarSolicitacaoPorRequisicaoExame = async (values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.postOpen('/solicitacoesexame/requisicao/cadastrar', values)
      return response.data.solicitacaoExame
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const editarSolicitacaoExame = async (id, values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.putOpen(`/solicitacoesexame/${id}/`, values)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarTipoProcedimentosAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`solicitacoesexame/tiposProcedimentos/`)
      return response.data.tiposProcedimentos
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarDespachosSolicitacoesAbertos = async (idSetor) => {
    try {
      const response = await fetch.get(`solicitacoesexame/despachos/abertos/${idSetor}`)
      return response.data.despachos
    } catch (err) {
      return
    }
  }

  const listarDespachosSolicitacoesConcluidos = async (idSetor) => {
    try {
      const response = await fetch.get(`solicitacoesexame/despachos/concluidos/${idSetor}`)
      return response.data.despachos
    } catch (err) {
      return
    }
  }

  const listarSolicitacoesRealizadasPeloSetor = async (idSetor) => {
    const response = await fetch.get(`solicitacoesexame/solicitadas/${idSetor}`)
    return response.data.solicitacoes
  }

  const concluirDespacho = async (id) => {
    try {
      await fetch.put(`solicitacoesexame/despacho/concluir/${id}`)
    } catch (err) {
      return
    }
  }

  const redespachar = async (id, idsSetores) => {
    try {
      await fetch.postOpen(`solicitacoesexame/despacho/despachar/${id}`, { idsSetores })
    } catch (err) {
      return
    }
  }

  const listarSolicitacaoSimplificadaAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`solicitacoesexame/detalharSolicitacaoExame/${id}/simplificada`)
      return response.data.solicitacaoExame
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesDeSolicitacaoAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`solicitacoesexame/detalharSolicitacaoExame/${id}/exames`)
      return response.data.solicitacaoExame?.exSols
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  } 

  return {
    listarSolicitacoesDeCasoGlobal,
    listarSolicitacoesDeCasoAsync,
    listarSolicitacaoExame,
    listarSolicitacaoExameAsync,
    listarSolicitacaoExameGlobal,
    listarSolicitacaoSimplificadaAsync,
    listarExamesDeSolicitacaoAsync,
    listarTiposSolicitacoesDeExameGlobal,
    deletarSolicitacaoExame,
    cadastrarSolicitacaoExame,
    editarSolicitacaoExame,
    listarTipoProcedimentosAsync,
    listarSolicitacoesRealizadasPeloSetor,
    listarDespachosSolicitacoesAbertos,
    listarDespachosSolicitacoesConcluidos,
    concluirDespacho,
    redespachar,
    cadastrarSolicitacaoPorRequisicaoExame,
  }
}
